import { METRES_PER_DEGREE_LAT, METRES_PER_DEGREE_LNG } from "./metres";

export interface KrigePoint {
  lng: number;
  lat: number;
  value: number;
}

const MIN_KRIGE_PTS = 4; // fewer than this can't support a kriging surface (matches M1c's ≥4/property)
export { MIN_KRIGE_PTS };

const mean = (v: number[]) => v.reduce((s, x) => s + x, 0) / v.length;
const variance = (v: number[]) => {
  const m = mean(v);
  return v.reduce((s, x) => s + (x - m) ** 2, 0) / v.length;
};

// solve A·w = b (square, small) by Gaussian elimination with partial pivoting; null if singular
function solve(A: number[][], b: number[]): number[] | null {
  const n = b.length;
  const M = A.map((row, i) => [...row, b[i]]);
  for (let col = 0; col < n; col++) {
    let piv = col;
    for (let r = col + 1; r < n; r++)
      if (Math.abs(M[r][col]) > Math.abs(M[piv][col])) piv = r;
    if (Math.abs(M[piv][col]) < 1e-12) return null;
    [M[col], M[piv]] = [M[piv], M[col]];
    for (let r = 0; r < n; r++) {
      if (r === col) continue;
      const f = M[r][col] / M[col][col];
      for (let c = col; c <= n; c++) M[r][c] -= f * M[col][c];
    }
  }
  return M.map((row, i) => row[n] / row[i]);
}

function idw(neighbors: { d: number; v: number }[]): number {
  let num = 0;
  let den = 0;
  for (const { d, v } of neighbors) {
    const w = 1 / (d * d + 1e-6);
    num += w * v;
    den += w;
  }
  return den > 0 ? num / den : neighbors[0].v;
}

export function ordinaryKrige(
  points: KrigePoint[],
  queries: { lng: number; lat: number }[],
  opts: { maxNeighbors?: number } = {},
): (number | null)[] {
  const pts = points.filter(
    (p) =>
      Number.isFinite(p.value) &&
      Number.isFinite(p.lng) &&
      Number.isFinite(p.lat),
  );
  if (pts.length === 0) return queries.map(() => null);
  if (pts.length < MIN_KRIGE_PTS) {
    // too sparse for kriging — nearest-value so a rec still renders
    return queries.map((q) => {
      let best = pts[0];
      let bd = Infinity;
      for (const p of pts) {
        const d = (p.lng - q.lng) ** 2 + (p.lat - q.lat) ** 2;
        if (d < bd) {
          bd = d;
          best = p;
        }
      }
      return best.value;
    });
  }

  // local metric coordinates (equirectangular — plenty accurate at field scale)
  const lat0 = mean(pts.map((p) => p.lat));
  const kx = METRES_PER_DEGREE_LNG * Math.cos((lat0 * Math.PI) / 180);
  const ky = METRES_PER_DEGREE_LAT;
  const X = pts.map((p) => ({ x: p.lng * kx, y: p.lat * ky, v: p.value }));

  // exponential variogram parameters from the data
  const sill = variance(X.map((p) => p.v)) || 1;
  const dists: number[] = [];
  for (let i = 0; i < X.length; i++)
    for (let j = i + 1; j < X.length; j++)
      dists.push(Math.hypot(X[i].x - X[j].x, X[i].y - X[j].y));
  dists.sort((a, b) => a - b);
  const range = Math.max(1, dists[Math.floor(dists.length / 2)]); // median pairwise distance
  const nugget = 0.05 * sill;
  // γ(h): 0 at h=0, ~95% sill at the practical range
  const gamma = (h: number) =>
    h <= 0 ? 0 : nugget + (sill - nugget) * (1 - Math.exp((-3 * h) / range));

  const maxN = Math.min(opts.maxNeighbors ?? 24, X.length);
  const lo = Math.min(...X.map((p) => p.v));

  return queries.map((q) => {
    const qx = q.lng * kx;
    const qy = q.lat * ky;
    const near = X.map((p, idx) => ({ idx, d: Math.hypot(p.x - qx, p.y - qy) }))
      .sort((a, b) => a.d - b.d)
      .slice(0, maxN);
    if (near[0].d < 1e-6) return X[near[0].idx].v; // exact hit

    const n = near.length;
    const A: number[][] = Array.from({ length: n + 1 }, () =>
      new Array<number>(n + 1).fill(0),
    );
    const b = new Array<number>(n + 1).fill(0);
    for (let i = 0; i < n; i++) {
      const pi = X[near[i].idx];
      for (let j = 0; j < n; j++) {
        const pj = X[near[j].idx];
        A[i][j] = gamma(Math.hypot(pi.x - pj.x, pi.y - pj.y));
      }
      A[i][n] = 1;
      A[n][i] = 1;
      b[i] = gamma(near[i].d);
    }
    b[n] = 1;
    const w = solve(A, b);
    const est = w
      ? near.reduce((s, nn, i) => s + w[i] * X[nn.idx].v, 0)
      : idw(near.map((nn) => ({ d: nn.d, v: X[nn.idx].v }))); // singular system → IDW
    // clamp to non-negative (soil concentrations); guard NaN from a degenerate solve
    return Number.isFinite(est)
      ? Math.max(lo < 0 ? est : 0, est)
      : idw(near.map((nn) => ({ d: nn.d, v: X[nn.idx].v })));
  });
}

/*
 *
 * It must be the SAME method, not merely a reasonable one: kriging a different surface here is what
 * produced a rec built on numbers that appear on no layer, which is the whole reason we moved to
 * the served columns.
 */
export function idwAnalyte(
  pts: { lng: number; lat: number; value: number }[],
  centroids: { lng: number; lat: number }[],
): (number | null)[] {
  if (!pts.length) return centroids.map(() => null);
  const lat0 = centroids.length
    ? centroids[Math.floor(centroids.length / 2)].lat
    : pts[0].lat;
  const cos = Math.cos((lat0 * Math.PI) / 180);
  return centroids.map((c) => {
    let num = 0;
    let den = 0;
    for (const p of pts) {
      const dx = (p.lng - c.lng) * cos;
      const dy = p.lat - c.lat;
      const d2 = dx * dx + dy * dy;
      if (d2 < 1e-14) return p.value; // exact hit: the sample's own value, not a weighted blur of it
      const w = 1 / d2;
      num += w * p.value;
      den += w;
    }
    return den > 0 ? num / den : null;
  });
}
